/**
 * TrustCenter — Trust score, penalties, appeals, suspension reviews (buyer/bidder).
 *
 * Data from GET /api/v1/trust/me/; appeals via POST /api/v1/trust/appeals/.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { apiClient } from '../../utils/auth';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import WarningIcon from '@mui/icons-material/Warning';
import GavelIcon from '@mui/icons-material/Gavel';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';

const VIOLATION_LABELS = {
  FAKE_BID: 'Fake bid',
  NON_DELIVERY: 'Non-delivery',
  IDENTITY_BREACH: 'Identity breach',
  KYC_FRAUD: 'KYC fraud',
  MALICIOUS_DISPUTE: 'Malicious dispute',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function penaltyAppealable(penalty, appeals) {
  if (!penalty.is_reversible) return false;
  if (penalty.appeal_deadline) {
    const end = new Date(penalty.appeal_deadline);
    end.setHours(23, 59, 59, 999);
    if (Date.now() > end.getTime()) return false;
  }
  const pid = penalty.id;
  if (appeals.some((a) => Number(a.penalty_record) === Number(pid))) return false;
  return true;
}

export default function TrustCenter() {
  const navigate = useNavigate();
  const role = typeof window !== 'undefined' ? localStorage.getItem('cmp_role') || '' : '';
  const accountStatus = typeof window !== 'undefined' ? localStorage.getItem('cmp_account_status') || '' : '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [appealPenaltyId, setAppealPenaltyId] = useState('');
  const [appealText, setAppealText] = useState('');
  const [appealSubmitting, setAppealSubmitting] = useState(false);
  const [appealMessage, setAppealMessage] = useState({ type: '', text: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: body } = await apiClient.get('/api/v1/trust/me/');
      setData(body);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('This page is only for buyers and bidders.');
      } else {
        setError('Could not load trust information. Try again or log in.');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (role !== 'BUYER' && role !== 'BIDDER') {
      setLoading(false);
      setError('Sign in as a buyer or bidder to use the trust dashboard.');
      return;
    }
    load();
  }, [load, role, accountStatus]);

  const handleSubmitAppeal = async (e) => {
    e.preventDefault();
    setAppealMessage({ type: '', text: '' });
    const pid = Number(appealPenaltyId);
    if (!pid) {
      setAppealMessage({ type: 'error', text: 'Select a penalty to appeal.' });
      return;
    }
    const trimmed = appealText.trim();
    if (trimmed.length < 20) {
      setAppealMessage({ type: 'error', text: 'Appeal explanation must be at least 20 characters.' });
      return;
    }
    setAppealSubmitting(true);
    try {
      await apiClient.post('/api/v1/trust/appeals/', {
        penalty_record_id: pid,
        appeal_text: trimmed,
      });
      setAppealText('');
      setAppealPenaltyId('');
      setAppealMessage({ type: 'success', text: 'Appeal submitted. An administrator will review it.' });
      await load();
    } catch (err) {
      const msg = err.response?.data?.error?.message || 'Could not submit appeal.';
      setAppealMessage({ type: 'error', text: msg });
    } finally {
      setAppealSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress aria-label="Loading trust information" />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', mt: 4, p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/login')}>
          Go to login
        </Button>
      </Box>
    );
  }

  const penalties = Array.isArray(data?.penalties) ? data.penalties : [];
  const appeals = Array.isArray(data?.appeal_requests) ? data.appeal_requests : [];
  const reviews = Array.isArray(data?.suspension_reviews) ? data.suspension_reviews : [];
  const appealable = penalties.filter((p) => penaltyAppealable(p, appeals));

  const percentage = (Number(data?.trust_score ?? 0) / 5.0) * 100;

  return (
    <Box sx={{ maxWidth: 850, mx: 'auto', mt: 4, px: 3, pb: 8 }}>
      {/* Premium Standardized Back Button */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{
          mb: 3,
          borderColor: 'rgba(99, 102, 241, 0.4)',
          color: '#818cf8',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
          }
        }}
      >
        Back
      </Button>

      {/* Header Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <ShieldIcon sx={{ fontSize: 38, color: '#818cf8', filter: 'drop-shadow(0 0 10px rgba(129,140,248,0.4))' }} />
        <Typography 
          variant="h4" 
          fontWeight={800} 
          sx={{ 
            color: '#f8fafc', 
            fontFamily: "'Space Grotesk', sans-serif",
            letterSpacing: '-0.02em'
          }}
        >
          Trust &amp; account standing
        </Typography>
      </Box>

      <Typography variant="body2" color="#94a3b8" sx={{ mb: 4, lineHeight: 1.6 }}>
        Your trust standing affects matching priority and marketplace access. Penalties are managed by platform administrators. Appeals can be submitted for eligible penalties, while suspension reviews are triggered if standing drops below critical levels.
      </Typography>

      {/* Current Standing Card (Trust Score Panel) */}
      <Card
        sx={{
          mb: 4,
          background: 'linear-gradient(135deg, rgba(13, 21, 38, 0.7) 0%, rgba(30, 41, 59, 0.7) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(129, 140, 248, 0.25)',
          borderRadius: 4,
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: '#818cf8',
            boxShadow: '0 0 25px rgba(129, 140, 248, 0.25)',
          }
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#f1f5f9" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon sx={{ color: '#818cf8', fontSize: 22 }} /> Account Security Standing
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Current Level: <strong>{data?.account_status || '—'}</strong>
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h3" fontWeight={800} sx={{ color: '#10b981', fontFamily: "'Space Grotesk', sans-serif", textShadow: '0 0 15px rgba(16,185,129,0.3)' }}>
                {Number(data?.trust_score ?? 0).toFixed(1)} <span style={{ fontSize: '18px', color: '#94a3b8', fontWeight: 500 }}>/ 5.0</span>
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Trust Rating Progress
              </Typography>
              <Typography variant="body2" color="#10b981" fontWeight={700}>
                {percentage.toFixed(0)}%
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 12, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Box sx={{ width: `${percentage}%`, height: '100%', borderRadius: 6, background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)', boxShadow: '0 0 15px rgba(16,185,129,0.5)' }} />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, mb: 4, alignItems: 'flex-start', bgcolor: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.15)', borderRadius: 3, p: 2.5 }}>
        <InfoIcon sx={{ color: '#818cf8', fontSize: 24, mt: 0.2 }} />
        <Typography variant="body2" color="#cbd5e1" sx={{ lineHeight: 1.6 }}>
          Admins apply penalties in Django admin or via <code>POST /api/v1/admin/penalties/</code>. They review appeals
          at <code>POST /api/v1/admin/appeals/&lt;id&gt;/review/</code>. You will see status updates here after refresh.
        </Typography>
      </Box>

      {/* Penalties Section */}
      <Card
        sx={{
          mb: 4,
          background: 'linear-gradient(135deg, rgba(13, 21, 38, 0.4) 0%, rgba(30, 41, 59, 0.4) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: 4,
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s',
          '&:hover': {
            borderColor: 'rgba(129, 140, 248, 0.2)',
          }
        }}
      >
        <CardContent sx={{ p: 3.5 }}>
          <Typography variant="h6" fontWeight={700} color="#f1f5f9" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, fontFamily: "'Space Grotesk', sans-serif" }}>
            <WarningIcon sx={{ color: '#f59e0b', fontSize: 20 }} /> Penalties on your account
          </Typography>
          {penalties.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No penalties recorded.</Typography>
          ) : (
            penalties.map((p, idx) => (
              <Box key={p.id} sx={{ py: 2, borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="#f8fafc">
                    {VIOLATION_LABELS[p.violation_type] || p.violation_type}
                  </Typography>
                  <Chip size="small" label={`-${p.deduction_amount} pts`} color="error" sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Applied: {formatDate(p.applied_at)}
                  {p.is_reversible ? ` · Appeal by: ${p.appeal_deadline || '—'}` : ' · Not appealable'}
                </Typography>
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      {/* Appeals Section */}
      <Card
        sx={{
          mb: 4,
          background: 'linear-gradient(135deg, rgba(13, 21, 38, 0.4) 0%, rgba(30, 41, 59, 0.4) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: 4,
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s',
          '&:hover': {
            borderColor: 'rgba(129, 140, 248, 0.2)',
          }
        }}
      >
        <CardContent sx={{ p: 3.5 }}>
          <Typography variant="h6" fontWeight={700} color="#f1f5f9" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, fontFamily: "'Space Grotesk', sans-serif" }}>
            <GavelIcon sx={{ color: '#818cf8', fontSize: 20 }} /> Your appeals
          </Typography>
          {appeals.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>No appeals submitted yet.</Typography>
          ) : (
            appeals.map((a, idx) => (
              <Box key={a.id} sx={{ py: 2, borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5, justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight={600} color="#e2e8f0">
                    Penalty #{a.penalty_record} ({VIOLATION_LABELS[a.penalty_violation_type] || a.penalty_violation_type})
                  </Typography>
                  <Chip 
                    size="small" 
                    label={a.status} 
                    color={a.status === 'APPROVED' ? 'success' : a.status === 'REJECTED' ? 'default' : 'warning'} 
                    sx={{ fontWeight: 600, fontSize: 11 }}
                  />
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#cbd5e1', bgcolor: 'rgba(255,255,255,0.02)', p: 2, borderRadius: 2, border: '1px solid rgba(255,255,255,0.04)', mb: 1 }}>
                  {a.appeal_text}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Submitted: {formatDate(a.submitted_at)}
                </Typography>
              </Box>
            ))
          )}

          {appealable.length > 0 && <Divider sx={{ my: 3.5, borderColor: 'rgba(255, 255, 255, 0.08)' }} />}

          {appealable.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#f8fafc" sx={{ mb: 2, fontFamily: "'Space Grotesk', sans-serif" }}>
                Submit a new appeal
              </Typography>
              <Box component="form" onSubmit={handleSubmitAppeal}>
                {appealMessage.text && (
                  <Alert severity={appealMessage.type === 'success' ? 'success' : 'error'} sx={{ mb: 2.5, borderRadius: 2 }}>
                    {appealMessage.text}
                  </Alert>
                )}
                <FormControl 
                  fullWidth 
                  size="small" 
                  sx={{ 
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      color: '#f1f5f9',
                      bgcolor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: 2,
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(129, 140, 248, 0.4)' },
                      '&.Mui-focused fieldset': { borderColor: '#818cf8' },
                    },
                    '& .MuiInputLabel-root': { color: '#94a3b8' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#818cf8' },
                  }}
                >
                  <InputLabel id="appeal-penalty-label">Penalty</InputLabel>
                  <Select
                    labelId="appeal-penalty-label"
                    label="Penalty"
                    value={appealPenaltyId}
                    onChange={(e) => setAppealPenaltyId(e.target.value)}
                  >
                    {appealable.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>
                        #{p.id} — {VIOLATION_LABELS[p.violation_type] || p.violation_type} (−{p.deduction_amount})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Explain why the penalty should be reviewed (min 20 characters)"
                  value={appealText}
                  onChange={(e) => setAppealText(e.target.value)}
                  fullWidth
                  multiline
                  minRows={4}
                  inputProps={{ maxLength: 8000 }}
                  sx={{ 
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      color: '#f1f5f9',
                      bgcolor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: 2,
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(129, 140, 248, 0.4)' },
                      '&.Mui-focused fieldset': { borderColor: '#818cf8' },
                    },
                    '& .MuiInputLabel-root': { color: '#94a3b8' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#818cf8' },
                  }}
                />
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={appealSubmitting}
                  sx={{
                    background: 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)',
                    color: '#fff',
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    py: 1.2,
                    px: 3.5,
                    '&:hover': {
                      background: 'linear-gradient(90deg, #818cf8 0%, #6366f1 100%)',
                      boxShadow: '0 0 15px rgba(99, 102, 241, 0.45)',
                    }
                  }}
                >
                  {appealSubmitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Submit appeal'}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Suspension Reviews Section */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(13, 21, 38, 0.4) 0%, rgba(30, 41, 59, 0.4) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: 4,
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s',
          '&:hover': {
            borderColor: 'rgba(129, 140, 248, 0.2)',
          }
        }}
      >
        <CardContent sx={{ p: 3.5 }}>
          <Typography variant="h6" fontWeight={700} color="#f1f5f9" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, fontFamily: "'Space Grotesk', sans-serif" }}>
            <HistoryIcon sx={{ color: '#818cf8', fontSize: 20 }} /> Suspension reviews
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
            When your trust score falls below a platform threshold after a penalty, CMP may open a{' '}
            <strong>suspension review</strong>. <strong>PENDING</strong> means staff are reviewing;
            <strong> ACTIONED</strong> or <strong>DISMISSED</strong> are final outcomes. Contact support if you have
            questions.
          </Typography>
          {reviews.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No suspension reviews on your account.</Typography>
          ) : (
            reviews.map((r, idx) => (
              <Box key={r.id} sx={{ py: 2, borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="body2" fontWeight={600} color="#f8fafc">Review #{r.id}</Typography>
                    <Chip size="small" label={r.status} sx={{ fontWeight: 600, fontSize: 11 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Score at trigger: {r.trigger_score} · {formatDate(r.created_at)}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
